import { createRouter, createWebHistory } from 'vue-router'
import { getEventDetails, getEventsList, rsvpStudentToEvent } from '@/API/events'
import { getProfile } from '@/API/profile'
import { getAuthToken } from '@/services/auth'
import { useAuthStore } from '@/store/auth'
import { useEventsStore, EVENT_RSVP_STATUS } from '@/store/events'
import { useUtilsStore } from '@/store/utils'
import { UserTypes } from '@/models/auth'
import { useProfileStore } from '@/store/profile'

const siteName = 'The Global Career Accelerator'

export const routes = [
  {
    path: '/',
    name: 'home',
    alias: '/email-verification/:userId/:token',
    component: () => import('../views/HomeView.vue'),
    meta: {
      title: 'Home',
      visibleUserTypes: [UserTypes['LOGGED_OUT_USER'], UserTypes['STUDENT']],
      hideToolbar: true,
    },
    beforeEnter: async (to, from, next) => {
      const eventsStore = useEventsStore()
      const authToken = getAuthToken()
      const allEventsResponse = getEventsList({ rsvped: false, endAfter: 'now' })

      let rsvpdEventsResponse = Promise.resolve({ data: {} })
      if (authToken) {
        if (eventsStore?.rsvpEventStatus === EVENT_RSVP_STATUS.TRIGGERED) {
          try {
            const response = await rsvpStudentToEvent(eventsStore.rsvpEventId)

            if (response.status < 200 || response.status >= 300) {
              eventsStore.setRsvpEventStatus(EVENT_RSVP_STATUS.FAILURE)
              throw new Error(`Request failed with status: ${response.status}`)
            }

            eventsStore.setRsvpEventStatus(EVENT_RSVP_STATUS.SUCCESS)
          } catch {
            // This will log to Sentry be default, so pass and try to grab events
          }
        }

        rsvpdEventsResponse = getEventsList({ rsvped: true, endAfter: 'now' })
      }

      return Promise.all([allEventsResponse, rsvpdEventsResponse])
        .then((responses) => {
          const eventsData = responses[0]?.data?.results || []
          to.meta.events = eventsData

          const rsvpedData = responses[1]?.data?.results || []
          to.meta.rsvpedEvents = rsvpedData

          next()
        })
        .catch(() => {
          to.meta.events = []
          to.meta.rsvpedEvents = []
          next()
        })
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: {
      title: 'Log In',
      visibleUserTypes: [UserTypes['LOGGED_OUT_USER']],
      hideToolbar: true,
      hideToolbarCTA: true,
    },
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('../views/SignupView.vue'),
    meta: {
      title: 'Sign Up',
      visibleUserTypes: [UserTypes['LOGGED_OUT_USER']],
      hideToolbar: true,
      hideToolbarCTA: true,
    },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('../views/AccountSettingsView.vue'),
    meta: {
      title: 'Settings',
      visibleUserTypes: [UserTypes['STUDENT']],
    },
    beforeEnter: async (to, from, next) => {
      const authStore = useAuthStore()
      const response = await getProfile(authStore.userId)

      to.meta.profileData = response.data
      next()
    },
  },
  {
    path: '/welcome',
    name: 'welcome',
    component: () => import('../views/WelcomeView.vue'),
    meta: {
      visibleUserTypes: [UserTypes['STUDENT']],
      hideToolbarCTA: true,
    },
  },
  {
    path: '/events/:eventId',
    name: 'eventDetail',
    component: () => import('../views/EventDetailView.vue'),
    meta: {
      visibleUserTypes: [UserTypes['LOGGED_OUT_USER'], UserTypes['STUDENT']],
    },
    beforeEnter: async (to, from, next) => {
      try {
        const response = await getEventDetails(to.params.eventId)

        if (response.status < 200 || response.status >= 300) {
          throw new Error(`Request failed with status: ${response.status}`)
        }

        const data = response.data
        to.meta.eventData = data

        document.title = `${data.title} - ${siteName}`

        next()
      } catch {
        next({
          name: 'notFound',
        })
      }
    },
  },
  {
    path: '/reset-password-request',
    name: 'resetPasswordRequest',
    component: () => import('../views/ResetPasswordRequestView.vue'),
    meta: {
      visibleUserTypes: [UserTypes['LOGGED_OUT_USER']],
      hideToolbarCTA: true,
    },
  },
  {
    path: '/reset-password/:token/:userId',
    name: 'resetPassword',
    component: () => import('../views/ResetPasswordView.vue'),
    meta: {
      visibleUserTypes: [UserTypes['LOGGED_OUT_USER'], UserTypes['STUDENT']],
      hideToolbarCTA: true,
    },
  },
  // IMPORTANT: The 404 route always needs to be declared LAST as a catch-all
  // in case it doesn't match with any of the defined routes above.
  {
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    component: () => import('../views/NotFoundView.vue'),
    meta: {
      visibleUserTypes: [UserTypes['LOGGED_OUT_USER'], UserTypes['STUDENT']],
      hideToolbar: true,
      hideToolbarCTA: true,
    },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const utilsStore = useUtilsStore()
  const profileStore = useProfileStore()

  utilsStore.setIsLoading(true)

  const authToken = getAuthToken()

  // Clear the pinia store if the auth cookie has expired
  if (!authToken && authStore.isAuthenticated) {
    authStore.$reset()
    profileStore.$reset()
  }

  // Important! Dynamic titles (e.g. event titles) must be handled in beforeEnter handlers.
  // Otherwise, set `meta.title` on the route definition
  if (to.meta.title) {
    document.title = `${to.meta.title} - ${siteName}`
  } else {
    document.title = siteName
  }

  // Capture the Salesforce ID if we don't have it stored
  if (to.query.sid && !profileStore.salesforceId) {
    profileStore.setSalesforceId(to.query.sid)
  }

  // redirect users to login if page requires login
  if (!authToken && !to.meta?.visibleUserTypes.includes(UserTypes['LOGGED_OUT_USER'])) {
    next({
      name: 'login',
      query: { ...to.query, continue: to.path },
    })
    return
  }
  // redirect users to home page if already logged in
  else if (authToken && !to.meta.visibleUserTypes.includes(UserTypes['STUDENT'])) {
    next({
      name: 'home',
      query: to.query,
    })
  }

  next()
})

router.afterEach(() => {
  const utilsStore = useUtilsStore()
  utilsStore.setIsLoading(false)
})

export default router
